import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, Category, UpdateCategoryData } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { ApiError, getErrorMessage, isNetworkError, getNetworkErrorMessage, type ErrorInfo } from '@/lib/errors';
import { QUERY_KEYS, QUERY_STALE_TIMES } from '@/lib/constants';
import { BUDGET_ASSIGNMENT_INVALID, CATEGORY_NOT_FOUND } from '@/lib/translations';
import { invalidateCategoryQueries } from '@/hooks/queryInvalidation';

interface UseCategoriesOptions {
  type?: 'income' | 'expense';
  enabled?: boolean;
}

export function useCategories(options: UseCategoriesOptions = {}) {
  const { type, enabled = true } = options;

  return useQuery({
    queryKey: [QUERY_KEYS.categories, type],
    queryFn: () => api.getCategories(type),
    enabled,
    staleTime: QUERY_STALE_TIMES.categories,
  });
}

interface CreateCategoryData {
  name: string;
  type: 'income' | 'expense';
}

export function useCreateCategory() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: CreateCategoryData) => api.createCategory(data),
    onSuccess: (newCategory: Category) => {
      invalidateCategoryQueries(queryClient);
      toast({
        title: 'Categoria criada',
        description: `${newCategory.name} foi adicionada com sucesso.`,
      });
    },
    onError: (error: unknown) => {
      const errorInfo = isNetworkError(error) ? getNetworkErrorMessage() : getErrorMessage(error);
      toast({
        title: errorInfo.title,
        description: errorInfo.description,
        variant: 'destructive',
      });
    },
  });
}

// The pt-BR copy for a failed category update. 404 and 422 are contextual (TRD §12.1): a 422 is only blamed
// on the classification when the patch carried a budget key, because any body-parse failure on this
// endpoint is a 422 too. Everything else keeps the generic copy.
export function categoryUpdateErrorInfo(error: unknown, patch: UpdateCategoryData): ErrorInfo {
  if (isNetworkError(error)) return getNetworkErrorMessage();
  if (error instanceof ApiError) {
    if (error.status === 404) return CATEGORY_NOT_FOUND;
    if (error.status === 422 && ('budget_treatment' in patch || 'budget_type' in patch)) return BUDGET_ASSIGNMENT_INVALID;
  }
  return getErrorMessage(error);
}

export function useUpdateCategory() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    // id routes the PUT; the rest is the partial patch body — kept separate so id never
    // leaks into the request body categoriesApi.update sends.
    mutationFn: ({ id, ...patch }: UpdateCategoryData & { id: string }) => api.updateCategory(id, patch),
    onSuccess: (updated: Category) => {
      // Seed every cached list from the response — the canonical rendering of the saved category — so a row
      // settles on it in the same render the mutation finishes, instead of flashing the old pair until the
      // refetch below lands.
      queryClient.setQueriesData<Category[]>({ queryKey: [QUERY_KEYS.categories] }, (list) =>
        list?.map((category) => (category.id === updated.id ? updated : category))
      );
      invalidateCategoryQueries(queryClient);
    },
    onError: (error: unknown, variables) => {
      // The category is gone, or was never ours (the backend reports ownership as 404): the list is stale.
      if (error instanceof ApiError && error.status === 404) invalidateCategoryQueries(queryClient);
      const errorInfo = categoryUpdateErrorInfo(error, variables);
      toast({
        title: errorInfo.title,
        description: errorInfo.description,
        variant: 'destructive',
      });
    },
  });
}
